import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Paper,
  Typography,
  alpha,
  Collapse,
} from '@mui/material';
import {
  Add,
  CalendarToday,
  AccessTime,
  Flag,
  Close,
  Repeat,
} from '@mui/icons-material';
import { parseTask, type ParsedTask } from '@/utils/taskParser';
import { brandColors } from '@/theme/colors';
import type { TaskPriority } from '@/types/todolist';
import { PRIORITY_COLORS } from '@/types/todolist';
import type { RecurrenceRule } from '@/types/recurrence';
import { getRecurrenceDescription } from '@/types/recurrence';
import { RecurrenceSelector } from './RecurrenceSelector';

type TaskInputProps = {
  onAdd: (
    title: string,
    dueDate: Date | null,
    priority: TaskPriority | null,
    recurrence: RecurrenceRule | null
  ) => void;
  disabled?: boolean;
  placeholder?: string;
};

export const TaskInput = ({
  onAdd,
  disabled = false,
  placeholder = 'Add a new task...',
}: TaskInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [parsedResult, setParsedResult] = useState<ParsedTask | null>(null);
  const [recurrence, setRecurrence] = useState<RecurrenceRule | null>(null);

  // Manual overrides - when user removes a parsed token
  const [overrides, setOverrides] = useState<{
    clearDate: boolean;
    clearPriority: boolean;
    clearRecurrence: boolean;
  }>({
    clearDate: false,
    clearPriority: false,
    clearRecurrence: false,
  });

  // Debounced parsing
  useEffect(() => {
    if (!inputValue.trim()) {
      setParsedResult(null);
      return;
    }

    const timer = setTimeout(() => {
      const result = parseTask(inputValue);
      setParsedResult(result);
    }, 150);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Reset overrides when input changes significantly
  useEffect(() => {
    setOverrides({ clearDate: false, clearPriority: false, clearRecurrence: false });
  }, [inputValue]);

  // Computed values with overrides applied
  const effectiveResult = useMemo(() => {
    if (!parsedResult) return null;
    return {
      ...parsedResult,
      dueDate: overrides.clearDate ? null : parsedResult.dueDate,
      hasTime: overrides.clearDate ? false : parsedResult.hasTime,
      priority: overrides.clearPriority ? null : parsedResult.priority,
      recurrence: overrides.clearRecurrence ? null : parsedResult.recurrence,
    };
  }, [parsedResult, overrides]);

  // Final recurrence: manual selection overrides parsed recurrence
  const effectiveRecurrence = useMemo(() => {
    if (recurrence) return recurrence;
    return effectiveResult?.recurrence || null;
  }, [recurrence, effectiveResult]);

  const hasTokens = useMemo(() => {
    if (!effectiveResult && !effectiveRecurrence) return false;
    return effectiveResult?.dueDate !== null || effectiveResult?.priority !== null || effectiveRecurrence !== null;
  }, [effectiveResult, effectiveRecurrence]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleAdd();
    }
  };

  const handleAdd = useCallback(() => {
    if (!inputValue.trim()) return;

    const title = effectiveResult?.title || inputValue.trim();
    const dueDate = effectiveResult?.dueDate || null;
    const priority = effectiveResult?.priority || null;

    onAdd(title, dueDate, priority, effectiveRecurrence);
    setInputValue('');
    setParsedResult(null);
    setRecurrence(null);
    setOverrides({ clearDate: false, clearPriority: false, clearRecurrence: false });
  }, [inputValue, effectiveResult, effectiveRecurrence, onAdd]);

  const handleClearDate = () => {
    setOverrides((prev) => ({ ...prev, clearDate: true }));
  };

  const handleClearPriority = () => {
    setOverrides((prev) => ({ ...prev, clearPriority: true }));
  };

  const handleClearRecurrence = () => {
    setRecurrence(null);
    setOverrides((prev) => ({ ...prev, clearRecurrence: true }));
  };

  const handleClearAll = () => {
    setOverrides({ clearDate: true, clearPriority: true, clearRecurrence: true });
    setRecurrence(null);
  };

  // Get display text for date token
  const getDateDisplayText = (): string => {
    if (!parsedResult) return '';
    const dateToken = parsedResult.tokens.find((t) => t.type === 'date' || t.type === 'time');
    return dateToken?.displayValue || '';
  };

  // Get display text for priority token
  const getPriorityDisplayText = (): string => {
    if (!parsedResult) return '';
    const priorityToken = parsedResult.tokens.find((t) => t.type === 'priority');
    return priorityToken?.displayValue || '';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: alpha(brandColors.purple, 0.03),
        border: '1px solid',
        borderColor: alpha(brandColors.purple, 0.1),
        borderRadius: 2,
      }}
    >
      {/* Input row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
        }}
      >
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          fullWidth
          size="small"
          disabled={disabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
            },
          }}
          helperText={
            <Typography
              component="span"
              variant="caption"
              sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline' } }}
            >
              Try: "tomorrow 3pm p1" or "next Monday"
            </Typography>
          }
        />
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <RecurrenceSelector
            value={recurrence}
            onChange={setRecurrence}
            disabled={disabled}
            variant="chip"
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={disabled || !inputValue.trim()}
            sx={{
              bgcolor: brandColors.purple,
              '&:hover': { bgcolor: brandColors.purpleDeep },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              minWidth: 100,
            }}
            startIcon={<Add />}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Preview panel */}
      <Collapse in={hasTokens}>
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: '1px dashed',
            borderColor: alpha(brandColors.purple, 0.15),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Parsed: <strong>{effectiveResult?.title || inputValue}</strong>
            </Typography>
            <Button
              size="small"
              onClick={handleClearAll}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: '0.7rem',
                color: 'text.secondary',
              }}
            >
              Clear All
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Date/Time chip */}
            {effectiveResult?.dueDate && !overrides.clearDate && (
              <Chip
                size="small"
                icon={
                  effectiveResult.hasTime ? (
                    <AccessTime sx={{ fontSize: '16px !important' }} />
                  ) : (
                    <CalendarToday sx={{ fontSize: '16px !important' }} />
                  )
                }
                label={getDateDisplayText()}
                onDelete={handleClearDate}
                deleteIcon={<Close sx={{ fontSize: '14px !important' }} />}
                sx={{
                  bgcolor: alpha(brandColors.purple, 0.1),
                  color: brandColors.purple,
                  '& .MuiChip-icon': { color: brandColors.purple },
                  '& .MuiChip-deleteIcon': {
                    color: brandColors.purple,
                    '&:hover': { color: brandColors.purpleDeep },
                  },
                }}
              />
            )}

            {/* Priority chip */}
            {effectiveResult?.priority && !overrides.clearPriority && (
              <Chip
                size="small"
                icon={<Flag sx={{ fontSize: '16px !important' }} />}
                label={getPriorityDisplayText()}
                onDelete={handleClearPriority}
                deleteIcon={<Close sx={{ fontSize: '14px !important' }} />}
                sx={{
                  bgcolor: alpha(PRIORITY_COLORS[effectiveResult.priority], 0.1),
                  color: PRIORITY_COLORS[effectiveResult.priority],
                  '& .MuiChip-icon': { color: PRIORITY_COLORS[effectiveResult.priority] },
                  '& .MuiChip-deleteIcon': {
                    color: PRIORITY_COLORS[effectiveResult.priority],
                    '&:hover': { color: PRIORITY_COLORS[effectiveResult.priority] },
                  },
                }}
              />
            )}

            {/* Recurrence chip */}
            {effectiveRecurrence && (
              <Chip
                size="small"
                icon={<Repeat sx={{ fontSize: '16px !important' }} />}
                label={getRecurrenceDescription(effectiveRecurrence)}
                onDelete={handleClearRecurrence}
                deleteIcon={<Close sx={{ fontSize: '14px !important' }} />}
                sx={{
                  bgcolor: alpha(brandColors.purple, 0.1),
                  color: brandColors.purple,
                  '& .MuiChip-icon': { color: brandColors.purple },
                  '& .MuiChip-deleteIcon': {
                    color: brandColors.purple,
                    '&:hover': { color: brandColors.purpleDeep },
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};
